import React from "react";
import {
    Box,
    Typography,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    CircularProgress,
    Tooltip,
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    Button,
    Divider,
    Select,
    FormControl,
} from "@mui/material";
import translations from "../../../constants/purchaseInvoices/translations/supplierInvoiceTranslations";
import { isApprovedStatus } from '../../../Utils/purchaseInvoices/statusTranslations';
import { APPROVAL_PACKAGE_STATUS } from '../../../constants/purchaseInvoices/approvalPackageConstants';
import StatusChip from '../shared/StatusChip';
import OverdueAttentionIndicator from '../shared/OverdueAttentionIndicator';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { Pencil } from 'lucide-react';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import IosShareIcon from "@mui/icons-material/IosShare";
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshIcon from '@mui/icons-material/Refresh';
import DynamicSearchBar from "../../../pages/purchaseInvoices/SearchBar";
import { fieldsToSearch } from '../../../constants/invoiceTableConstants';
import { isRequiresAttentionStatus } from '../../../Utils/purchaseInvoices/statusTranslations';
import { INVOICE_SORT_FIELDS } from '../../../constants/purchaseInvoices/invoiceManagement/invoiceTableConstants';
import {
    StyledTableContainer,
    StyledTableRow,
    StyledTableCell,
    StyledTableHeadCell,
    StyledNoResultsOverlay,
} from "../../../styles/purchaseInvoices/pages/commonTableStyles";
import styles from '../../../styles/purchaseInvoices/pages/InvoiceTableStyles';

function InvoiceDesktopView({
    hookData,
    actionMenuAnchorEl,
    activeInvoiceId,
    onActionsClick,
    onActionsClose,
    onOpenDrawer,
    onOpenManualDrawer,
    canEdit,
    canDelete,
    canUpload,
    onCreateGoodsReceipt,
}) {
    const {
        openSearch,
        setOpenSearch,
        activeSearches,
        setActiveSearches,
        globalSearchTerm,
        page,
        rowsPerPage,
        orderBy,
        order,
        checkingBooknum,
        highlightedInvoiceRef,
        setOpenDelete,
        setSelectedFileName,
        statuses,
        selectedStatus,
        setSelectedStatus,
        sortedData,
        paginatedData,
        hasGlobalSearch,
        hasNoSearchResults,
        onRowClick,
        handleCheckGoodsReceipt,
        handleRetryApprovedInvoice,
        handleRetryRequiresAttentionInvoice,
        handleChangeInvoiceStatus,
        handleGlobalSearch,
        handleSearch,
        handleRequestSort,
        handleEditClick,
        handleExportToExcel,
        isAdmin,
        setPage,
        setRowsPerPage,
        canDeleteInvoice,
        isOverdueAttention,
        viewAll,
        setViewAll,
        canViewAll
    } = hookData;

    const renderActions = (invoice) => {
        const isChecking = checkingBooknum === invoice.id;

        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                minWidth: '60px'
            }}>
                {isChecking && (
                    <CircularProgress size={20} color="primary" />
                )}
                <Tooltip title="פעולות" arrow>
                    <IconButton
                        onClick={(e) => onActionsClick(e, invoice.id)}
                        size="small"
                        sx={styles.actionsMenuButton}
                        disabled={isChecking}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    return (
        <>
            <Box sx={styles.headerBox}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                    <Button
                        variant="contained"
                        onClick={onOpenDrawer}
                        size="medium"
                        startIcon={<CloudUploadIcon />}
                        sx={styles.uploadButtonStyled(true)}
                        disabled={!canUpload()}
                    >
                        העלה חשבונית חדשה
                    </Button>

                    <Button
                        variant="contained"
                        onClick={onOpenManualDrawer}
                        size="medium"
                        startIcon={<EditIcon />}
                        sx={{
                            borderRadius: '8px',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                            background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1565c0 0%, #0c3d8a 100%)',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                transform: 'translateY(-1px)'
                            }
                        }}
                        disabled={!canUpload()}
                    >
                        הזנת חשבונית ידנית
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleExportToExcel}
                        startIcon={<IosShareIcon />}
                        sx={styles.exportButton}
                    >
                        ייצוא לאקסל
                    </Button>

                    {canViewAll && (
                        <Button
                            variant="outlined"
                            size="medium"
                            color="secondary"
                            onClick={() => setViewAll(v => !v)}
                            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                        >
                            {viewAll ? 'הצג את החשבוניות שלי בלבד' : 'הצג את כל חשבוניות הקבוצה'}
                        </Button>
                    )}
                </Box>

                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    width: '100%'
                }}>
                    <FormControl sx={{ minWidth: 280, width: 280, flexShrink: 0 }}>
                        <Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            size="small"
                            dir="rtl"
                            sx={{
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                height: '42px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d1d5db'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#9ca3af'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#6b7280',
                                    borderWidth: '2px'
                                },
                                '& .MuiSelect-select': {
                                    py: 1.25
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        minWidth: 280,
                                        maxWidth: 400,
                                        mt: 0.5,
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        '& .MuiMenuItem-root': {
                                            fontSize: '0.95rem',
                                            fontWeight: 500,
                                            py: 1.25,
                                            px: 2,
                                            whiteSpace: 'normal',
                                            wordWrap: 'break-word',
                                            '&:hover': {
                                                backgroundColor: '#f3f4f6'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#e5e7eb',
                                                color: '#111827',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    backgroundColor: '#d1d5db'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            {statuses.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status === "all" ? "כל הסטטוסים" : status}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        placeholder="חיפוש..."
                        value={globalSearchTerm}
                        onChange={(e) => handleGlobalSearch(e.target.value)}
                        size="small"
                        sx={styles.globalSearchInput(true)}  
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: globalSearchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleGlobalSearch('')}
                                        sx={styles.globalSearchClearButton}  
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        onClick={() => setOpenSearch(true)}
                        variant="outlined"
                        startIcon={<SearchIcon />}
                        sx={styles.advancedSearchButton(true)}  
                    >
                        חיפוש מתקדם
                    </Button>
                </Box>

                {(hasGlobalSearch || Object.keys(activeSearches).length > 0) && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        pt: 1
                    }}>
                        {hasGlobalSearch && (
                            <Chip
                                label={`חיפוש: ${globalSearchTerm}`}
                                onDelete={() => handleGlobalSearch('')}
                                size="small"
                                sx={styles.globalSearchChip}
                            />
                        )}
                        {Object.entries(activeSearches).map(([key, value]) => {
                            const fieldLabel = fieldsToSearch.find(f => f.name === key)?.name || key;
                            return (
                                <Chip
                                    key={key}
                                    label={`${fieldLabel}: ${value}`}
                                    onDelete={() => {
                                        const newSearches = { ...activeSearches };
                                        delete newSearches[key];
                                        setActiveSearches(newSearches);
                                        handleSearch(newSearches, true);
                                    }}
                                    size="small"
                                    sx={styles.advancedSearchChip}
                                />
                            );
                        })}
                    </Box>
                )}
            </Box>

            {openSearch && (
                <Box sx={styles.searchBarContainerStyled}>
                    <DynamicSearchBar
                        onClose={() => setOpenSearch(false)}
                        fields={fieldsToSearch}
                        onSearch={handleSearch}
                    />
                </Box>
            )}

            <StyledTableContainer sx={styles.tableContainer}>
                {hasNoSearchResults ? (
                    <StyledNoResultsOverlay>
                        <Typography variant="body1">
                            אין תוצאות לחיפוש זה
                        </Typography>
                    </StyledNoResultsOverlay>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.ID}
                                        direction={orderBy === INVOICE_SORT_FIELDS.ID ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.ID)}
                                    >
                                        {translations.id}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.SUPPLIER_NAME}
                                        direction={orderBy === INVOICE_SORT_FIELDS.SUPPLIER_NAME ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.SUPPLIER_NAME)}
                                    >
                                        {translations.supplierName}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.DATE}
                                        direction={orderBy === INVOICE_SORT_FIELDS.DATE ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.DATE)}
                                    >
                                        {translations.date}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.UPLOADED_AT}
                                        direction={orderBy === INVOICE_SORT_FIELDS.UPLOADED_AT ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.UPLOADED_AT)}
                                    >
                                        {translations.uploadedAt}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.AMOUNT}
                                        direction={orderBy === INVOICE_SORT_FIELDS.AMOUNT ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.AMOUNT)}
                                    >
                                        {translations.amount}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.AMOUNT_BEFORE_VAT}
                                        direction={orderBy === INVOICE_SORT_FIELDS.AMOUNT_BEFORE_VAT ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.AMOUNT_BEFORE_VAT)}
                                    >
                                        {translations.amountBeforeVat}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.STATUS}
                                        direction={orderBy === INVOICE_SORT_FIELDS.STATUS ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.STATUS)}
                                    >
                                        {translations.status}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    <TableSortLabel
                                        active={orderBy === INVOICE_SORT_FIELDS.APPROVER}
                                        direction={orderBy === INVOICE_SORT_FIELDS.APPROVER ? order : 'asc'}
                                        onClick={() => handleRequestSort(INVOICE_SORT_FIELDS.APPROVER)}
                                    >
                                        {translations.approver}
                                    </TableSortLabel>
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center">
                                    {translations.actions}
                                </StyledTableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.length === 0 ? (
                                <TableRow>
                                    <StyledTableCell colSpan={9} align="center">
                                        <Typography variant="body1" color="text.secondary">
                                            אין חשבוניות להצגה
                                        </Typography>
                                    </StyledTableCell>
                                </TableRow>
                            ) : (
                                paginatedData.map((invoice) => (
                                    <StyledTableRow
                                        key={invoice.id}
                                        ref={invoice.id === hookData.highlightedInvoiceId ? highlightedInvoiceRef : null}
                                        onClick={() => onRowClick(invoice)}
                                        sx={{
                                            ...(isOverdueAttention(invoice) ? styles.requiresAttentionRow : {}),
                                            ...(invoice.id === hookData.highlightedInvoiceId ? styles.highlightAnimation : {}),
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                                cursor: 'pointer',
                                            }
                                        }}
                                    >
                                        <StyledTableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                {invoice.booknum}
                                                {invoice.errorDetails && (
                                                    <Tooltip
                                                        title={
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                                    {invoice.errorDetails.hebrewMessage}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {invoice.errorDetails.tooltip}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        arrow
                                                    >
                                                        <WarningAmberIcon
                                                            sx={{
                                                                color: invoice.errorDetails.severity === 'ERROR' ? '#E65100' : 'warning.main',
                                                                fontSize: 20,
                                                                cursor: 'help'
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">{invoice.supplierName}</StyledTableCell>
                                        <StyledTableCell align="center">{invoice.date ? invoice.date.replace(/\./g, '/') : '-'}</StyledTableCell>
                                        <StyledTableCell align="center">
                                            {invoice.uploadedAt ?
                                                new Date(invoice.uploadedAt).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '/') :
                                                (invoice.date ? invoice.date.replace(/\./g, '/') : '-')
                                            }
                                        </StyledTableCell>
                                        <StyledTableCell align="center">{Number(invoice.amount).toLocaleString()}</StyledTableCell>
                                        <StyledTableCell align="center">{invoice.amountBeforeVat != null ? Number(invoice.amountBeforeVat).toLocaleString() : '-'}</StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                {isOverdueAttention(invoice) && (
                                                    <OverdueAttentionIndicator invoice={invoice} />
                                                )}
                                                {invoice.status === 'ממתין לקבלת סחורה' && onCreateGoodsReceipt ? (
                                                    <Tooltip title="ליצירת קבלת סחורה לחץ כאן" arrow placement="top">
                                                        <Box
                                                            component="span"
                                                            onClick={(e) => { e.stopPropagation(); onCreateGoodsReceipt(invoice); }}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <StatusChip invoice={invoice} styles={styles} />
                                                        </Box>
                                                    </Tooltip>
                                                ) : (
                                                    <StatusChip invoice={invoice} styles={styles} />
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">{invoice.approver || '-'}</StyledTableCell>
                                        <StyledTableCell align="center">
                                            {renderActions(invoice)}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
            </StyledTableContainer>

            <Box sx={styles.paginationContainer}>
                <TablePagination
                    component="div"
                    count={sortedData.length}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="שורות בעמוד"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
                />
            </Box>

            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={onActionsClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: styles.actionsMenu
                }}
            >
                {activeInvoiceId && paginatedData.find(inv => inv.id === activeInvoiceId) && (() => {
                    const invoice = paginatedData.find(inv => inv.id === activeInvoiceId);
                    const isPendingGoods = invoice.status === "ממתין לקבלת סחורה";
                    const isApproved = isApprovedStatus(invoice.status);
                    const requiresAttention = isRequiresAttentionStatus(invoice.status);
                    const isRequiresAttentionForRetry =
                        requiresAttention &&
                        invoice.approvalPackage?.status === APPROVAL_PACKAGE_STATUS.APPROVED_PRE_PRIORITY &&
                        invoice.approvalPackage?.priorityCreationFailed === true &&
                        invoice.approvalPackage?.allowRetry === true;

                    return (
                        <>
                            <MenuItem onClick={(e) => {
                                e.stopPropagation();
                                onRowClick(invoice);
                                onActionsClose();
                            }}>
                                <ReceiptLongIcon sx={styles.menuItemIcon} color="primary" />
                                <Typography sx={styles.menuItemText}>צפה בפרטי חשבונית</Typography>
                            </MenuItem>

                            <MenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(invoice);
                                onActionsClose();
                            }} disabled={!canEdit()}>
                                <Pencil size={18} color={canEdit() ? "#6B7280" : "#ccc"} />
                                <Typography sx={styles.menuItemText}>ערוך חשבונית</Typography>
                            </MenuItem>

                            {requiresAttention && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(invoice);
                                    onActionsClose();
                                }}>
                                    <AutorenewIcon sx={{ ...styles.menuItemIcon, color: '#E65100' }} />
                                    <Typography sx={{ ...styles.menuItemText, color: '#E65100' }}>עבד מחדש חשבונית</Typography>
                                </MenuItem>
                            )}

                            {isRequiresAttentionForRetry && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetryRequiresAttentionInvoice(invoice.id);
                                    onActionsClose();
                                }}>
                                    <SendIcon sx={styles.menuItemIcon} color="primary" />
                                    <Typography sx={styles.menuItemText}>שליחה חוזרת לפריוריטי</Typography>
                                </MenuItem>
                            )}

                            {isApproved && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetryApprovedInvoice(invoice.id);
                                    onActionsClose();
                                }}>
                                    <SendIcon sx={styles.menuItemIcon} color="primary" />
                                    <Typography sx={styles.menuItemText}>שליחה חוזרת לפריוריטי</Typography>
                                </MenuItem>
                            )}

                            {isPendingGoods && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckGoodsReceipt(invoice.id);
                                    onActionsClose();
                                }} disabled={checkingBooknum === invoice.id}>
                                    {checkingBooknum === invoice.id ? (
                                        <CircularProgress size={18} color="primary" />
                                    ) : (
                                        <RefreshIcon sx={styles.menuItemIcon} color="primary" />
                                    )}
                                    <Typography sx={styles.menuItemText}>בדוק קבלת סחורה</Typography>
                                </MenuItem>
                            )}

                            {isPendingGoods && onCreateGoodsReceipt && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    onCreateGoodsReceipt(invoice);
                                    onActionsClose();
                                }}>
                                    <AddBoxIcon sx={styles.menuItemIcon} color="success" />
                                    <Typography sx={styles.menuItemText}>יצירת קבלת סחורה</Typography>
                                </MenuItem>
                            )}

                            {isAdmin && (invoice.status === 'אושר ונקלט' || invoice.status === 'draft_in_priority') && (
                                <MenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeInvoiceStatus(invoice.id, 'מאושר');
                                    onActionsClose();
                                }}>
                                    <ChangeCircleIcon sx={{ ...styles.menuItemIcon, color: 'grey.700' }} />
                                    <Typography sx={styles.menuItemText}>שנה סטטוס למאושר</Typography>
                                </MenuItem>
                            )}

                            <Divider />

                            <Tooltip
                                title={
                                    !canDeleteInvoice(invoice)
                                        ? "אין אפשרות למחוק חשבונית בסטטוס אושר ונקלט "
                                        : !canDelete()
                                            ? "אין לך הרשאה למחוק חשבונית זו"
                                            : "מחק חשבונית"
                                }
                                arrow
                                placement="left"
                            >
                                <span>
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFileName(invoice.fileName);
                                            setOpenDelete(true);
                                            onActionsClose();
                                        }}
                                        disabled={!canDelete() || !canDeleteInvoice(invoice)}
                                        sx={styles.deleteMenuItem}
                                    >
                                        <DeleteIcon sx={styles.menuItemIcon} />
                                        <Typography sx={styles.menuItemText}>מחק חשבונית</Typography>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        </>
                    );
                })()}
            </Menu>
        </>
    );
}

export default InvoiceDesktopView;